import axios from 'axios';
import { unstable_cache } from 'next/cache';
import { cache } from 'react';

import { ICollection, IPagination } from '@/types';
import { Article } from '@/features/article/components/article-card';
import { BACKEND_API_URL } from '@/lib/public-api-url';

export const api = axios.create({
  baseURL: BACKEND_API_URL,
});

// Interceptor для автоматического разлогинивания при ошибках авторизации
// api.interceptors.response.use(
//   (response) => response,
//   async (error) => {
//     // Проверяем, является ли ошибка связанной с авторизацией
//     if (
//       error.response?.status === 401 || // Unauthorized
//       error.response?.status === 403 || // Forbidden
//       error.response?.data?.message?.toLowerCase().includes("unauthorized") ||
//       error.response?.data?.message?.toLowerCase().includes("forbidden") ||
//       error.response?.data?.message?.toLowerCase().includes("invalid token") ||
//       error.response?.data?.message?.toLowerCase().includes("expired token") ||
//       error.response?.data?.message?.toLowerCase().includes("access denied")
//     ) {
//       // Автоматически разлогиниваем пользователя
//       try {
//         await signOut({ redirect: false })
//         // signOut автоматически обновляет состояние аутентификации
//         // Дополнительное обновление страницы не требуется
//       } catch (signOutError) {
//         console.error("Error during automatic logout:", signOutError)
//       }
//     }

//     return Promise.reject(error)
//   }
// )

export async function getMainArticles() {
  try {
    const res = await api.get('/api/articles?where[main][equals]=true');
    return res.data.docs || [];
  } catch (error) {
    console.error('Error fetching main articles:', error);
    return [];
  }
}

// Кешированная версия getMainArticles с временем жизни кеша 5 минут
// Сортировка выполняется внутри функции кеширования, чтобы не сортировать при каждом рендере
// React.cache обеспечивает per-request дедупликацию
export const getMainArticlesCached = cache(async () => {
  return unstable_cache(
    async () => {
      try {
        const res = await api.get('/api/articles?where[main][equals]=true');
        const articles = res.data.docs || [];
        // Сортируем по slideOrder один раз при получении данных из API
        return [...articles].sort(
          (a: Article, b: Article) => (a.slideOrder || 0) - (b.slideOrder || 0),
        );
      } catch (error) {
        console.error('Error fetching main articles:', error);
        return [];
      }
    },
    ['main-articles'],
    {
      revalidate: 300, // 5 минут в секундах
    },
  )();
});

export async function getCollectionsList() {
  try {
    const res = await api.get('/api/collections');
    return res.data.docs as ICollection[];
  } catch (error) {
    console.error('Error fetching main articles:', error);
    return [];
  }
}

// Кешированная версия getCollectionsList с временем жизни кеша 5 минут
// React.cache обеспечивает per-request дедупликацию
export const getCollectionsListCached = cache(async () => {
  return unstable_cache(
    async () => {
      try {
        const res = await api.get('/api/collections');
        return res.data.docs as ICollection[];
      } catch (error) {
        console.error('Error fetching collections:', error);
        return [];
      }
    },
    ['collections-list'],
    {
      revalidate: 300, // 5 минут в секундах
    },
  )();
});

// Функция для получения квизов с пагинацией
export async function getQuizzes(page: number = 1, limit: number = 20) {
  try {
    const res = await api.get(`/api/quizzes-custom?page=${page}&limit=${limit}&sort=-createdAt`);
    return res.data || { docs: [], hasNextPage: false, page: 1, totalPages: 1, totalDocs: 0 };
  } catch (error) {
    console.error('Error fetching quizzes:', error);
    return { docs: [], hasNextPage: false, page: 1, totalPages: 1, totalDocs: 0 };
  }
}

// Кешированная версия getQuizzes с временем жизни кеша 5 минут
// React.cache обеспечивает per-request дедупликацию
export const getQuizzesCached = cache(async (page: number = 1, limit: number = 20) => {
  return unstable_cache(
    async () => {
      try {
        const res = await api.get(
          `/api/quizzes-custom?page=${page}&limit=${limit}&sort=-createdAt`,
        );
        return res.data || { docs: [], hasNextPage: false, page: 1, totalPages: 1, totalDocs: 0 };
      } catch (error) {
        console.error('Error fetching quizzes:', error);
        return { docs: [], hasNextPage: false, page: 1, totalPages: 1, totalDocs: 0 };
      }
    },
    ['quizzes', page.toString(), limit.toString()],
    {
      revalidate: 300, // 5 минут в секундах
    },
  )();
});

// Функция для получения статей по коллекции с пагинацией
export async function getArticlesByCollection(slug: string, page: number = 1, limit: number = 10) {
  try {
    const res = await api.get(`/api/collections-articles/${slug}?page=${page}&limit=${limit}`);
    return res.data || { docs: [], hasNextPage: false, page: 1, totalPages: 1 };
  } catch (error) {
    console.error('Error fetching articles by collection:', error);
    return { docs: [], hasNextPage: false, page: 1, totalPages: 1 };
  }
}
